import { type Node, type XYPosition } from '@xyflow/react'
import { cloneDeep } from 'lodash'
import { type IBaseWorkflowData, type IWorkflowUiSettingsV2 } from 'entities/Workflow'

const ACTION_TYPE_ADD_TAGS = 'addTags'
const ACTION_TYPE_CHOICE = 'choice'
const ACTION_TYPE_REMOVE_TAGS = 'removeTags'
const ACTION_TYPE_SEND = 'sendMessage'
const ACTION_TYPE_SEND_RVM = 'sendRVM'
const ACTION_TYPE_TIME = 'time'
const ACTION_TYPE_DELAY = 'sleep'

interface ISelectOption {
  label: string
  key: string | number
  value?: string | number
}

interface IFilterArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

interface IFilters {
  filters: IFilterArgs[]
}

export interface IWorkflowV1Node extends Node {
  id: string
  type: string
  data: {
    id?: string
    type?: string
    label?: string
    branchName?: string
    noneMetBranchName?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataList?: any
    isDisableAvailable?: boolean
    isDisable?: boolean
    filters?: IFilters[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events?: any
  }
  height?: number
  position: XYPosition
  children?: IWorkflowV1Node[]
}

interface IObject {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

const checkIfKeyExist = (object: IObject, keyName: string) => {
  const result = Object.keys(object).filter((key) => key === keyName)

  return result[0]
}

interface IStepDefinitionArgs {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export const transformWorkflowV1UISettingsToV2 = (
  initialData: IWorkflowV1Node[] | null
): {
  definition: IBaseWorkflowData['definition']
  ui_settings?: IBaseWorkflowData['ui_settings']
} => {
  const data_v2: IWorkflowUiSettingsV2 = {
    branchNames: {},
  }
  let definition: IBaseWorkflowData['definition'] = {
    trigger: {
      id: 'contactEvent',
    },
    steps: {},
  }
  const getFlowArgs = (data: IFilters[]) => {
    const filtersGroup = data?.map((filter) => {
      const filters = filter?.filters?.map((filter) => {
        return filter
      })

      return { filters: filters }
    })

    return {
      filters: filtersGroup,
    }
  }

  const parser = (data: IWorkflowV1Node[]) => {
    data?.forEach((item) => {
      if (item.type === 'trigger' && item.data.filters) {
        definition.trigger = {
          id: 'contactEvent',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          args: getFlowArgs(item.data.filters),
        }
      }
      if (['connectorFinishLine'].includes(item.type) && definition) {
        const temporary = cloneDeep(definition)

        if (
          item.type === 'connectorFinishLine' &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          temporary.steps[item.id] = {
            type: 'result',
            id: 'success',
          }

          definition = temporary
        }
      }

      if (['actionsBlock'].includes(item.type) && definition && item.children) {
        const temporary = cloneDeep(definition)

        if (
          item.data.type === ACTION_TYPE_SEND &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          temporary.steps[item.id] = {
            type: 'action',
            id: ACTION_TYPE_SEND,
            args: {
              inboxId: item.data?.dataList?.teamId,
              body: item.data?.dataList?.message,
            },
            next: item.children[0].id,
          }

          definition = temporary
        }

        if (
          item.data.type === ACTION_TYPE_SEND_RVM &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          temporary.steps[item.id] = {
            type: 'action',
            id: ACTION_TYPE_SEND_RVM,
            args: {
              inboxId: item.data?.dataList?.teamId,
              audioUrl: item.data?.dataList?.source,
            },
            next: item.children[0].id,
          }

          definition = temporary
        }

        if (
          item.data.type === ACTION_TYPE_TIME &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          const { days, hours, minutes } = item.data?.dataList
          const daysValue = days > 1 ? `${days} days` : `${days} day`
          const hoursValue = hours !== 0 ? `${hours} hour` : ''
          const minutesValue = minutes !== 0 ? `${minutes} minute` : ''
          const timeout = `${daysValue} ${hoursValue} ${minutesValue}`

          temporary.steps[item.id] = {
            type: 'control',
            id: 'awaitReply',
            args: {
              timeout: timeout.trim(),
              onInput: item.children[0].id,
              onTimeout: item.children[0].id,
            },
          }

          definition = temporary
        }

        if (
          item.data.type === ACTION_TYPE_DELAY &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          const { days, hours, minutes } = item.data?.dataList
          const daysValue = days > 1 ? `${days} days` : `${days} day`
          const hoursValue = hours !== 0 ? `${hours} hour` : ''
          const minutesValue = minutes !== 0 ? `${minutes} minute` : ''
          const duration = `${daysValue} ${hoursValue} ${minutesValue}`

          temporary.steps[item.id] = {
            type: 'control',
            id: ACTION_TYPE_DELAY,
            args: {
              duration: duration.trim(),
              onWakeup: item.children[0].id,
            },
          }

          definition = temporary
        }

        if (
          item.children &&
          item.data.type === ACTION_TYPE_CHOICE &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          data_v2.branchNames = {
            ...data_v2.branchNames,
            [item.id]: {
              default: item.children?.[1]?.data.branchName || item.data.noneMetBranchName || 'None',
              choice: item.data.dataList[0]?.branchName || 'Branch 1',
            },
          }
          const choices: IStepDefinitionArgs = {}

          item.children.forEach((node, index) => {
            if (item.children && item.children.length - 1 !== index) {
              choices[node.id] = item?.data?.dataList[index]?.filters || []
            }
          })

          temporary.steps[item.id] = {
            type: 'control',
            id: ACTION_TYPE_CHOICE,
            args: {
              choices,
              default: item.children[item.children.length - 1].id,
            },
          }

          definition = temporary
        }

        if (
          item.data.type &&
          [ACTION_TYPE_ADD_TAGS, ACTION_TYPE_REMOVE_TAGS].includes(item.data.type) &&
          (!temporary.steps.hasOwnProperty(item.id) ||
            (temporary.steps.hasOwnProperty(item.id) &&
              checkIfKeyExist(temporary.steps, item.id) === item.id))
        ) {
          temporary.steps[item.id] = {
            type: 'action',
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            id: item.data.type,
            args: {
              ids: item.data.dataList.tags?.map((tag: ISelectOption) => tag.value),
            },
            next: item.children[0].id,
          }

          definition = temporary
        }
      }

      if (item.children) parser(item.children)
    })
  }

  if (initialData) {
    parser(initialData)
  }

  return {
    definition,
    ui_settings: {
      data: null,
      data_v2,
    },
  }
}
